import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { BarChart3, XCircle } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/EmptyState';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { useMetadataFilterDefinitions } from '@/components/metadata-filters/useMetadataFilterDefinitions';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useStore } from '@/store';
import { removeOrgPrefix } from '@/utils/routes';

import { AgentsPerformanceTab } from './components/AgentsPerformanceTab';
import { AnalyticsLoadingSkeleton } from './components/AnalyticsLoadingSkeleton';
import { OrgPerformanceTab } from './components/OrgPerformanceTab';
import { useAnalyticsFiltersState } from './hooks/useAnalyticsFiltersState';
import { resolveAnalyticsOrganizationId } from './utils/resolveOrg';

export const AnalyticsContainer: React.FC = () => {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId?: string }>();
  const {
    organizations,
    programs,
    selectedOrganizationId,
    isLoadingOrgAndPrograms,
    unauthorizedAccess,
  } = useOrgAndPrograms();
  const timeZone = useStore(state => state.getTimeZone());
  const routeOrganizationId = orgId ? removeOrgPrefix(orgId) : null;
  const effectiveOrganizationId = resolveAnalyticsOrganizationId(
    routeOrganizationId,
    selectedOrganizationId,
  );

  const {
    tab,
    setTab,
    programIds,
    setProgramIds,
    callDirections,
    setCallDirections,
    start,
    end,
    setDateRange,
    metadata,
    setMetadataFilter,
  } = useAnalyticsFiltersState({ timeZone });

  const { definitions: metadataDefinitions } =
    useMetadataFilterDefinitions(effectiveOrganizationId);

  const activeTab = tab;

  const programOptions = useMemo(
    () =>
      programs
        .filter(p => !!p.id)
        .map(p => ({ label: p.name || p.id || '', value: p.id! }))
        .sort((l, r) => l.label.localeCompare(r.label)),
    [programs],
  );
  const organizationName = useMemo(
    () =>
      organizations.find(organization => organization.id === effectiveOrganizationId)?.name ??
      effectiveOrganizationId ??
      '',
    [effectiveOrganizationId, organizations],
  );

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>Analytics</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  if (unauthorizedAccess?.isUnauthorized) {
    return (
      <>
        {breadcrumb}
        <EmptyState
          title="Access Denied"
          description="You do not have access to this organization. Please contact your administrator if you believe this is an error."
          icon={XCircle}
          iconClassName="text-muted-foreground"
          action={{ label: 'Go to Home', onClick: () => navigate('/') }}
        />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      <div className="flex h-full flex-col">
        <header className="flex items-start gap-3 px-6 pt-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <BarChart3 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Analytics</h1>
            <p className="text-sm text-muted-foreground">
              Deep-dive workspace — performance analysis across scopes.
            </p>
          </div>
        </header>

        <div className="px-6 pt-4">
          <Tabs value={activeTab} onValueChange={value => setTab(value as 'org' | 'agents')}>
            <TabsList className="bg-transparent p-0 gap-2 border-b rounded-none w-full justify-start h-auto">
              <TabsTrigger
                data-analytics-id="analytics:tab:org"
                value="org"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-2"
              >
                Organization Performance
              </TabsTrigger>
              <TabsTrigger
                data-analytics-id="analytics:tab:agents"
                value="agents"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none pb-2"
              >
                Agents Performance
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex-1 overflow-auto px-6 py-6">
          {!effectiveOrganizationId || isLoadingOrgAndPrograms ? (
            <AnalyticsLoadingSkeleton />
          ) : activeTab === 'org' ? (
            <OrgPerformanceTab
              organizationId={effectiveOrganizationId}
              organizationName={organizationName}
              programOptions={programOptions}
              selectedProgramIds={programIds}
              onProgramFilterChange={setProgramIds}
              selectedCallDirections={callDirections}
              onCallDirectionsChange={setCallDirections}
              start={start}
              end={end}
              timeZone={timeZone}
              onDateRangeChange={setDateRange}
              metadataDefinitions={metadataDefinitions}
              selectedMetadata={metadata}
              onMetadataFilterChange={setMetadataFilter}
            />
          ) : (
            <AgentsPerformanceTab
              organizationId={effectiveOrganizationId}
              organizationName={organizationName}
              programOptions={programOptions}
              selectedProgramIds={programIds}
              onProgramFilterChange={setProgramIds}
              start={start}
              end={end}
              timeZone={timeZone}
              onDateRangeChange={setDateRange}
            />
          )}
        </div>
      </div>
    </>
  );
};
